import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { adminApi } from "@/services/admin";

type AdminSessionState = "loading" | "unauthenticated" | "authenticated";

type AdminSessionContextValue = {
  status: AdminSessionState;
  username: string | null;
  csrfToken: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AdminSessionContext = createContext<AdminSessionContextValue | undefined>(undefined);

function readCsrfCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const entry = document.cookie
    .split(";")
    .map((segment) => segment.trim())
    .find((segment) => segment.startsWith("sc_admin_csrf="));
  if (!entry) return null;
  return decodeURIComponent(entry.split("=", 1)[1] ?? "");
}

export function AdminSessionProvider({ children }: { children: React.ReactNode }) {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  const { data, refetch, status } = useQuery({
    queryKey: ["admin", "session"],
    queryFn: () => adminApi.readSession(),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (status === "success" && data?.authenticated) {
      setUsername(data.username ?? null);
      const cookieToken = readCsrfCookie();
      setCsrfToken((prev) => data.csrfToken ?? cookieToken ?? prev);
    } else if (status === "success") {
      setUsername(null);
      setCsrfToken(null);
    }
  }, [status, data]);

  const login = useCallback(async (usernameInput: string, password: string) => {
    const session = await adminApi.createSession(usernameInput, password);
    setUsername(session.username);
    setCsrfToken(session.csrfToken);
    await refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    if (csrfToken) {
      try {
        await adminApi.deleteSession(csrfToken);
      } catch (error) {
        console.error(error);
      }
    }
    setUsername(null);
    setCsrfToken(null);
    await refetch();
  }, [csrfToken, refetch]);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo<AdminSessionContextValue>(
    () => ({
      status: status === "pending" ? "loading" : data?.authenticated ? "authenticated" : "unauthenticated",
      username,
      csrfToken,
      login,
      logout,
      refresh,
    }),
    [status, data?.authenticated, username, csrfToken, login, logout, refresh],
  );

  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>;
}

export function useAdminSession(): AdminSessionContextValue {
  const context = useContext(AdminSessionContext);
  if (!context) {
    throw new Error("useAdminSession must be used within AdminSessionProvider");
  }
  return context;
}
